/**
 * Public payment-confirmed landing — `/payment-confirmed` (UNAUTHENTICATED).
 *
 * The redirect target set as the Razorpay payment-link `callback_url` (and the
 * PayPal return). A payer lands here AFTER checkout. This page is a PURELY
 * friendly branded thank-you — it NEVER verifies a payment or calls an API; the
 * webhook reconciles server-side. It reads the redirect query params only to
 * tailor the message, and degrades to a calm generic thank-you when params are
 * missing (never an error / Not-Found).
 *
 * Same AV-green style as the pay page (reuses pay.css).
 */
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { resolvePaymentConfirmation, type ConfirmState } from '../lib/paySession';
import '../styles/pay.css';

function ConfirmHeader() {
  return (
    <div className="pp-header">
      <div className="pp-header-top">
        <div className="pp-wordmark">
          AAYATAN VEDA
          <span className="wm-sub">Vastu &amp; Architecture</span>
        </div>
        <span className="pp-secure">
          <span aria-hidden="true">🔒</span> Secure payment
        </span>
      </div>
      <div className="pp-for">Payment confirmation</div>
    </div>
  );
}

const COPY: Record<ConfirmState, { ico: string; title: string; msg: string }> = {
  confirmed: {
    ico: '✓',
    title: 'Payment received',
    msg: "Thank you — your payment has been received. We'll be in touch shortly.",
  },
  pending: {
    ico: '⏳',
    title: "We're confirming your payment",
    msg: "Thank you — you'll get a confirmation shortly. There's nothing more you need to do.",
  },
  cancelled: {
    ico: '↩',
    title: 'Payment not completed',
    msg: "Your payment wasn't completed. If you'd like to pay, please reopen the payment link from your quotation and try again.",
  },
  generic: {
    ico: '🌿',
    title: 'Thank you',
    msg: "If you've just paid, your confirmation is on its way. There's nothing more you need to do.",
  },
};

export default function PaymentConfirmed() {
  const [params] = useSearchParams();
  const { state, paymentRef } = useMemo(() => {
    const q: Record<string, string> = {};
    params.forEach((v, k) => {
      q[k] = v;
    });
    return resolvePaymentConfirmation(q);
  }, [params]);
  const c = COPY[state];

  return (
    <div className="pp-root">
      <div className="pp-card">
        <ConfirmHeader />
        <div className="pp-state">
          <div
            className={`pp-state-ico${state === 'cancelled' ? ' neutral' : ''}`}
            aria-hidden="true"
          >
            {c.ico}
          </div>
          <h1 className="pp-state-title">{c.title}</h1>
          <p className="pp-state-msg">{c.msg}</p>
          {state === 'confirmed' && paymentRef && (
            <p className="pp-gstin" style={{ marginTop: 14 }}>
              Reference: {paymentRef}
            </p>
          )}
        </div>
        <div className="pp-body" style={{ paddingTop: 0 }}>
          <div className="pp-foot">
            <span className="lk" aria-hidden="true">🔒</span>
            <span>Your quotation is reconciled automatically once payment clears — you can safely close this page.</span>
          </div>
        </div>
      </div>
      <p className="pp-caption">Aayatan Veda — Vastu &amp; Architecture</p>
    </div>
  );
}
